import React from "react";
import { Shield, ChevronRight } from "lucide-react";
import PremiumHeader from "../components/ui/PremiumHeader";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import { useEvents } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import { splitEventsByTimeline } from "../utils/eventSections";

interface TournamentsViewProps {
  onStageClick: (name: string, eventId: string, isEventEnded: boolean) => void;
  onNotificationClick: () => void;
}

const TournamentsView: React.FC<TournamentsViewProps> = ({
  onStageClick,
  onNotificationClick,
}) => {
  const { events, isLoading, error } = useEvents();
  const { mypage } = useMypageContext();
  const { upNext, schedule, pastEvents } = splitEventsByTimeline(events);

  const renderEventRow = (
    event: (typeof events)[number],
    variant: "upNext" | "schedule" | "past" = "schedule",
  ) => {
    const cardClass =
      variant === "past"
        ? "bg-slate-50 border border-slate-200 border-l-[3px] border-l-slate-300 hover:bg-slate-100"
        : variant === "upNext"
          ? "bg-white border border-slate-100 border-l-[3px] border-l-amber-500 hover:bg-amber-50"
          : "bg-white border border-slate-100 border-l-[3px] border-l-amber-200 hover:bg-amber-50";
    const titleClass =
      variant === "past"
        ? "text-slate-600 group-hover:text-slate-900"
        : "text-slate-700 group-hover:text-amber-600";
    const chevronClass =
      variant === "past"
        ? "text-slate-200 group-hover:text-slate-400"
        : "text-slate-200 group-hover:text-amber-400";

    return (
      <div
        key={event.id}
        onClick={() =>
          onStageClick(
            `${event.tournament} ${event.title}`,
            event.id,
            event.status === "finished",
          )
        }
        className={`flex items-center justify-between p-3 mb-2 rounded-xl cursor-pointer transition-all group shadow-sm active:scale-[0.98] ${cardClass}`}
      >
        <div className="text-left min-w-0 flex-1">
          <div className="flex flex-col items-start gap-0.5">
            {event.status === "finished" && (
              <span className="inline-flex items-center text-[9px] font-black bg-slate-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                イベント終了
              </span>
            )}
            {event.tournament && (
              <span
                className={`text-[9px] font-bold uppercase tracking-widest ${
                  variant === "past" ? "text-slate-400" : "text-amber-500"
                }`}
              >
                {event.tournament}
              </span>
            )}
            <p
              className={`font-bold transition-colors tracking-tight whitespace-normal break-words leading-snug ${titleClass}`}
            >
              {event.title}
            </p>
            <p className="text-xs text-slate-400">{event.date}</p>
          </div>
        </div>
        <ChevronRight
          className={`w-5 h-5 transition-colors shrink-0 ${chevronClass}`}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <div className="overflow-y-auto scrollbar-hide flex-1">
        <PremiumHeader
          title={UI_TEXT.nav.tournaments}
          icon={Shield}
          theme="amber"
          onNotificationClick={onNotificationClick}
        />
        <div className="px-6 py-6 pb-40 text-left">
          <MembershipStatsCard
            points={mypage?.yearly_point ?? "-"}
            rank={mypage?.yearly_rank ?? "-"}
            loginId={mypage?.login_id}
            dartsliveName={mypage?.dartslive_card_name}
            eventGroupName={mypage?.event_group?.name}
            theme="amber"
          />
          <h2 className="text-2xl font-black text-slate-800 tracking-tighter uppercase mb-4 pl-1">
            イベント一覧
          </h2>
          <p className="text-xs text-slate-400 mb-8 pl-1">
            {UI_TEXT.tournaments.tournamentCreationNotice}
          </p>

          {isLoading && (
            <p className="text-slate-400 text-sm text-center py-10">
              読み込み中...
            </p>
          )}
          {error && (
            <p className="text-rose-400 text-sm text-center py-10">{error}</p>
          )}

          {!isLoading && !error && upNext && (
            <>
              <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.upNext}
              </h3>
              {renderEventRow(upNext, "upNext")}
            </>
          )}
          {!isLoading && !error && schedule.length > 0 && (
            <>
              <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.schedule}
              </h3>
              {schedule.map((ev) => renderEventRow(ev, "schedule"))}
            </>
          )}
          {!isLoading && !error && pastEvents.length > 0 && (
            <>
              <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-10 mb-4 pl-1 text-left">
                {UI_TEXT.dashboard.pastEvents}
              </h3>
              {pastEvents.map((ev) => renderEventRow(ev, "past"))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentsView;
